import { Box, Select, Text, Title } from '@nimbus-ds/components';
import {
  CogIcon,
  EditIcon,
  HomeIcon,
  MarketingIcon,
  MenuIcon,
  MoneyIcon,
  SidebarIcon,
  StatsIcon,
} from '@nimbus-ds/icons';
import { AppShell, Menu, SideModal } from '@nimbus-ds/patterns';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../i18n/index.js';
import { useAppState } from '../state/AppState.js';
import type { Route } from '../routes.js';

const LANGUAGE_LABELS: Record<(typeof SUPPORTED_LANGUAGES)[number], string> = {
  es: 'Espanol',
  pt: 'Portugues',
  en: 'English',
};

// Sub-pages under the "Sales Booster" nav accordion. The first is its landing page (where the
// parent link goes); the whole list drives the parent's active/open state. Add upsells etc. here.
const BOOSTER_ROUTES: Route[] = ['quantity-offers'];

const NAV_COLLAPSED_KEY = 'cod-nav-collapsed';

// App icon shown in the menu header (and the only thing shown there when the rail is collapsed).
// Also the dashboard welcome banner's mark — one URL so the two cannot drift apart.
export const LOGO_URL =
  'https://cdn.shopify.com/app-store/listing_images/59ff1b17b1415cf11af2ee0a00c68334/icon/CKKYs5695_ICEAE=.png';

/**
 * App shell: a Nimbus sidebar (Menu) inside the AppShell layout. Top-level destinations
 * are Dashboard, Form Designer and Settings (sidebar, not tabs, because they are distinct
 * destinations and it matches the Nuvemshop admin pattern). The header carries the
 * language switcher, which persists the choice to the store row.
 */
export function Shell({
  route,
  onNavigate,
  children,
}: {
  route: Route;
  onNavigate: (route: Route) => void;
  children: ReactNode;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { settings, saveSettings } = useAppState();
  const [boosterOpen, setBoosterOpen] = useState(BOOSTER_ROUTES.includes(route));
  const [formOpen, setFormOpen] = useState(route === 'designer' || route === 'shipping-rates');
  // Sidebar collapse: false collapses the menu to a 48px icon rail (hovering a button shows its
  // label / sub-items in a popover). Persisted across reloads so the choice sticks.
  const [menuExpanded, setMenuExpanded] = useState(() => {
    try {
      return localStorage.getItem(NAV_COLLAPSED_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const toggleMenu = (): void =>
    setMenuExpanded((expanded) => {
      const next = !expanded;
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, next ? '0' : '1');
      } catch {
        /* private mode / storage disabled — collapse still works for this session */
      }
      return next;
    });
  // Collapsed rail: the logo turns into the expand button on hover (Nuvemshop-style).
  const [headerHover, setHeaderHover] = useState(false);

  const logoImg = (
    <img
      src={LOGO_URL}
      alt={t('nav.appName')}
      width={28}
      height={28}
      style={{ display: 'block', flex: '0 0 auto', borderRadius: 6 }}
    />
  );
  // A small, borderless icon toggle (no IconButton circle/border) — matches the Nuvemshop admin.
  const collapseButton = (
    <button
      type="button"
      onClick={toggleMenu}
      aria-label={menuExpanded ? t('nav.collapse') : t('nav.expand')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        color: '#6b7280',
        borderRadius: 6,
        flex: '0 0 auto',
      }}
    >
      <SidebarIcon size={16} />
    </button>
  );

  /**
   * Built as a function so the DRAWER can render the very same tree with its own navigate — one
   * definition, so a destination added to the rail cannot go missing on mobile. The drawer is
   * always expanded and has no collapse button: a 280px overlay has nothing to collapse into.
   */
  const buildMenu = (
    navigate: (next: Route) => void,
    expanded: boolean,
    showCollapse: boolean,
  ): JSX.Element => (
    <Menu expanded={expanded}>
      <Menu.Header>
        {expanded ? (
          // Expanded: logo + name on the left (aligned with the nav-link icons below via the
          // Menu.Header's own padding), collapse button pushed to the far right.
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap="2"
          >
            <Box display="flex" alignItems="center" gap="2">
              {logoImg}
              <Title as="h4">{t('nav.appName')}</Title>
            </Box>
            {showCollapse ? collapseButton : null}
          </Box>
        ) : (
          // Collapsed rail: just the logo, which becomes the expand button on hover.
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            paddingY="2"
            onMouseEnter={() => setHeaderHover(true)}
            onMouseLeave={() => setHeaderHover(false)}
          >
            {headerHover ? collapseButton : logoImg}
          </Box>
        )}
      </Menu.Header>
      <Menu.Body>
        <Menu.Button
          label={t('nav.dashboard')}
          startIcon={HomeIcon}
          active={route === 'dashboard'}
          onClick={() => navigate('dashboard')}
        />
        <Menu.ButtonAccordion
          contentid="form-designer"
          open={formOpen}
          menuButton={{
            label: t('nav.designer'),
            startIcon: EditIcon,
            active: route === 'designer' || route === 'shipping-rates',
            // One click opens the designer AND reveals the sub-nav (no double-click).
            onClick: () => {
              setFormOpen(true);
              navigate('designer');
            },
          }}
        >
          {expanded ? (
            <Menu.Button
              label={t('nav.shippingRates')}
              active={route === 'shipping-rates'}
              onClick={() => navigate('shipping-rates')}
            />
          ) : (
            // Collapsed rail: Nimbus's hover popover lists only the accordion's children, so the
            // icon would otherwise read "Shipping rates". Show just the parent label instead —
            // the sub-items become available once the rail is expanded.
            <Menu.Button
              label={t('nav.designer')}
              active={route === 'designer' || route === 'shipping-rates'}
              onClick={() => navigate('designer')}
            />
          )}
        </Menu.ButtonAccordion>
        <Menu.ButtonAccordion
          contentid="sales-booster"
          open={boosterOpen}
          menuButton={{
            label: t('nav.salesBooster'),
            startIcon: MoneyIcon,
            active: BOOSTER_ROUTES.includes(route),
            // One click opens the sub-nav AND lands on its first page (Quantity Offers).
            onClick: () => {
              setBoosterOpen(true);
              navigate(BOOSTER_ROUTES[0]!);
            },
          }}
        >
          <Menu.Button
            label={t('nav.quantityOffers')}
            active={route === 'quantity-offers'}
            onClick={() => navigate('quantity-offers')}
          />
        </Menu.ButtonAccordion>
        <Menu.Button
          label={t('nav.analytics')}
          startIcon={StatsIcon}
          active={route === 'analytics'}
          onClick={() => navigate('analytics')}
        />
        <Menu.Button
          label={t('nav.tracking')}
          startIcon={MarketingIcon}
          active={route === 'tracking'}
          onClick={() => navigate('tracking')}
        />
        <Menu.Button
          label={t('nav.settings')}
          startIcon={CogIcon}
          active={route === 'settings'}
          onClick={() => navigate('settings')}
        />
      </Menu.Body>
    </Menu>
  );

  const menu = buildMenu(onNavigate, menuExpanded, true);

  const languageSwitcher = (
    <Box display="flex" alignItems="center" gap="2">
      <Text fontSize="caption">{t('common.language')}</Text>
      <Select
        id="lang-switch"
        name="lang-switch"
        value={settings?.admin_language ?? i18n.language}
        onChange={(e) => {
          const lang = e.target.value as (typeof SUPPORTED_LANGUAGES)[number];
          void i18n.changeLanguage(lang);
          void saveSettings({ admin_language: lang });
        }}
      >
        {SUPPORTED_LANGUAGES.map((lng) => (
          <Select.Option key={lng} value={lng} label={LANGUAGE_LABELS[lng]} />
        ))}
      </Select>
    </Box>
  );

  /**
   * 🚨 MOBILE NAVIGATION, and it has to live outside `AppShell`.
   *
   * Nimbus hides the menu slot below the `md` breakpoint — `menuProperties` defaults to
   * `{ display: { xs: 'none', md: 'block' } }`, measured in `@nimbus-ds/patterns` and stated in
   * their AppShell docs — so on a phone the sidebar computes to `display: none` and the merchant
   * cannot leave the page they landed on. Their docs suggest a fixed bottom bar "por ejemplo";
   * this app takes the other common answer, and deliberately:
   *
   *  - **Seven destinations, two of them nested, do not fit a phone tab bar.** A bar that scrolled
   *    sideways to reach Settings was worse than no bar — reported as such — and a bar that
   *    dropped the overflow would leave Shipping rates and Tracking Pixels unreachable.
   *  - **A fixed strip costs vertical space on every page, forever.** It sat over the end of the
   *    Dashboard's order list and the bottom of Analytics. 🚨 The obvious remedy — a spacer after
   *    `{children}` — silently does nothing: `AppShell.Body` is `display: flex` with no
   *    `flex-direction`, so a spacer becomes a COLUMN BESIDE the page, not room beneath it.
   *    A drawer has no such cost: it is an overlay that exists only while open.
   *  - **It reuses `menu` verbatim**, so the phone gets the same destinations, the same accordions
   *    and the same active states as the desktop rail, with nothing to keep in sync.
   */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigateFromDrawer = (next: Route): void => {
    setDrawerOpen(false);
    onNavigate(next);
  };

  const menuTrigger = (
    <Box display={{ xs: 'block', md: 'none' }}>
      <button
        type="button"
        data-probe="mobile-menu"
        aria-label={t('nav.appName')}
        aria-expanded={drawerOpen}
        onClick={() => setDrawerOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          border: 'none',
          borderRadius: 6,
          background: 'transparent',
          cursor: 'pointer',
          color: 'var(--nimbus-colors-neutral-textHigh)',
        }}
      >
        <MenuIcon size={20} />
      </button>
    </Box>
  );

  return (
    <AppShell menu={menu} menuExpanded={menuExpanded}>
      <AppShell.Header leftSlot={menuTrigger} rightSlot={languageSwitcher} />
      <AppShell.Body>{children}</AppShell.Body>
      {/* No `maxWidth`: Nuvemshop's design guidelines say this component "behaves differently in a
          mobile context, occupying the entire screen width", and capping it at 280px was overriding
          the behaviour their own docs describe. It only ever renders below `md`, where full width
          is the documented answer. */}
      <SideModal
        open={drawerOpen}
        onRemove={() => setDrawerOpen(false)}
        position="left"
        paddingBody="none"
        title={t('nav.appName')}
      >
        {buildMenu(navigateFromDrawer, true, false)}
      </SideModal>
    </AppShell>
  );
}
